import ReportVariable from '../../../types/report/ReportVariable';
import systemVariables from '../../../infrastructure/constants/functions/systemVariables';
import { SelectSearchOption } from 'react-select-search';
import copyArray from '../../../infrastructure/helpers/common/copyArray';
import operandHelper from '../../../infrastructure/helpers/functions/operand/operandHelper';
import Assignment from '../../../types/functions/Assignment/Assignment';
import Lookup from '../../../types/functions/Lookup';
import Operand from '../../../types/functions/Operand/Operand';
import GlobalModuleConfig from '../../../types/report/GlobalModuleConfig';
import Mode from '../../../types/report/Mode';
import ReportConfig from '../../../types/report/ReportConfig';
import sortingHelper from '../../../infrastructure/helpers/common/sortingHelper';
import IdNameAndNumber from '../../../types/report/IdNameAndNumber';
import IngestionConfig from '../../../types/ingestion/IngestionConfig';
import UserDefinedFunction from '../../../types/report/UserDefinedFunction';
import { getByAssignmentReferencesInGlobalConfig } from '../GlobalModule/GlobalModule.Helpers';
import ComponentType from '../../../types/report/ComponentType';
import httpClient from '../../../infrastructure/helpers/common/httpClient';
import Component from '../../../types/common/Component';
import RefDataSource from '../../../types/report/RefDataSource';
import assignmentHelper from '../../../infrastructure/helpers/functions/assignment/assignmentHelper';
import operandType from '../../../infrastructure/constants/functions/operandType';
import { getHightestNumberInCollection } from '../../../infrastructure/helpers/common/collectionOperationHelper';

const getVariableReferences = async (variable: ReportVariable, reportConfigId: number): Promise<Component[]> => {
    let component = { componentInnerType: ComponentType.Variable, componentType: ComponentType.Variable, componentId: variable?.id, reportConfigId, componentNumber: variable?.number } as Component;

    let res = await httpClient.post('report/global-module/references', component);

    return res.data as Component[];
}

const resolveSystemVariableNameSuggestion = (variables: ReportVariable[]): string => {

    for (let i = 0; i < systemVariables.names.length; i++) {

        if (!variables.some(x => x.isSystem && x.name === systemVariables.names[i])) {

            return systemVariables.names[i];
        }
    }

    return systemVariables.names[0];
};

const prepareOptionsFromVariables = (variables: ReportVariable[]): SelectSearchOption[] => {

    let options: SelectSearchOption[] = [];
    variables.forEach((variable) => {
        options.push({
            value: variable.name,
            name: variable.name,
            items: [{ name: variable.number.toString(), value: variable.number.toString() }]
        });
    });

    return options;
}

const prepareGlobalVariables = (globalVariables: ReportVariable[], configDataSourceId: number, configVariables: ReportVariable[], dataSources: IngestionConfig[]): ReportVariable[] => {

    if (!globalVariables || globalVariables.length === 0) {
        return [];
    }

    let usedGlobalVariablesIds = configVariables.filter(x => x.mode === Mode.Global).map(x => x.globalVariableModuleId);

    let variables = globalVariables.filter(variable => !usedGlobalVariablesIds.includes(variable.id as number) &&
        (!variable.dataSourceId || (variable.dataSourceId && variable.dataSourceId === configDataSourceId)));

    variables = variables.sort((x, y) => x.number > y.number ? 1 : -1);

    let fields = dataSources.find(x => x.id === configDataSourceId)?.fieldDefinitions?.map(x => x.id as number) || [];

    let results = [] as ReportVariable[];
    variables.forEach((variable) => {

        let qualify = qualifyForOption(variable.assignment, fields);

        if (qualify) {
            results.push(variable);
        }
    });

    return results;

}
const prepareOptionsFromGlobalVariables = (globalVariables: ReportVariable[], configDataSourceId: number, configVariables: ReportVariable[], dataSources: IngestionConfig[]): SelectSearchOption[] => {

    let options: SelectSearchOption[] = [];

    let variables = prepareGlobalVariables(globalVariables, configDataSourceId, configVariables, dataSources);

    variables.forEach((variable) => {

        options.push({
            name: variable.name,
            value: variable?.id ? variable.id.toString() : '',
            items: [{ name: variable.name, value: variable?.id ? variable.id.toString() : '' }]
        });
    });

    return options;
}

const qualifyForOption = (assignment: Assignment, fieldIds: number[]): boolean => {

    let qualify = true;

    operandHelper.proceedWhitAssignment(assignment, (operand: Operand) => {
        if (qualify && operand.fieldId && !fieldIds.includes(operand.fieldId)) {
            qualify = false;
        }
    });

    return qualify;
}

const findReferencedComponentsInVariables = (globalModuleConfig: GlobalModuleConfig,
    reportConfig: ReportConfig,
    globalVariableModuleId: number,
    dataSourcesIds: Set<number>,
    referencedLookups: Lookup[],
    referencedVariables: ReportVariable[],
    referencedUdfs: UserDefinedFunction[],
    missingDataSourcesMap: Map<number, string>): void => {

    let variableNumber = globalModuleConfig.variables.find(x => x.id === globalVariableModuleId)?.number as number;

    let lookupsIds = new Set<number>(reportConfig.lookups.map(x => x.globalLookupModuleId as number));

    let variablesIds = new Set<number>(reportConfig.variables.map(x => x.globalVariableModuleId as number));

    let udfsIds = new Set<number>(reportConfig.userDefinedFunctions.map(x => x.globalUDFModuleId as number));

    getReferencesInGlobalConfig(globalModuleConfig, variableNumber, dataSourcesIds, referencedVariables, referencedLookups, referencedUdfs, lookupsIds, variablesIds, udfsIds);

    if (dataSourcesIds.size || referencedVariables.length) {

        let configDataSources: number[] = reportConfig.refDataSources.map(x => x.id);

        dataSourcesIds.forEach((dataSourceId) => {
            if (!configDataSources.includes(dataSourceId)) {
                let ds = globalModuleConfig.refDataSources.find(x => x.id === dataSourceId);

                missingDataSourcesMap.set(dataSourceId, ds?.name as string);
            }
        });

        referencedVariables.forEach((v) => {
            let variableAssignmentOperands = assignmentHelper.getAllOperands(v.assignment);

            variableAssignmentOperands.forEach((o) => {
                if (o.fieldId && o.operandType === operandType.field) {
                    let dataSource = findDataSourceIdByFieldId(o.fieldId, globalModuleConfig.refDataSources);

                    if (dataSource) {
                        v.dataSourceId = dataSource.id;
                        missingDataSourcesMap.set(dataSource?.id, dataSource?.name as string);
                    }
                }
            })
        })
    }
}


const findDataSourceIdByFieldId = (fieldId: number, refDataSources: RefDataSource[]): RefDataSource | null => {

    for (let index = 0; index < refDataSources.length; index++) {
        const ds = refDataSources[index];

        if (ds.fieldDefinitions?.map(x => x.id).includes(fieldId)) {
            return ds;
        }
    }

    return null;
}

const addCurentGlobalLookupNumbersToMap = (reportConfigLookups: Lookup[], globalLookups: Lookup[], lookupsOldAndNewNumbers: Map<number, number>) => {
    reportConfigLookups.forEach((lookup) => {
        let globalLookup = globalLookups.find(x => x.id === lookup.globalLookupModuleId);

        if (globalLookup) {
            lookupsOldAndNewNumbers.set(globalLookup.number, lookup.number);
        }
    })
}

const updateVariableAssignmentForLookups = (variable: ReportVariable, lookupOldAndNewNumbers: Map<number, number>) => {
    operandHelper.proceedWhitAssignment(variable.assignment, (operand: Operand) => {
        if (operand.lookupNumber as number > 0 && lookupOldAndNewNumbers.has(operand.lookupNumber as number)) {

            operand.lookupNumber = lookupOldAndNewNumbers.get(operand.lookupNumber as number) as number;
        }
    });
}

const updateVariableAssignmentForVariables = (variable: ReportVariable, variablesOldAndNewNumbers: Map<number, number>) => {
    operandHelper.proceedWhitAssignment(variable.assignment, (operand: Operand) => {
        if (operand.variableNumber as number > 0 && variablesOldAndNewNumbers.has(operand.variableNumber as number)) {

            operand.variableNumber = variablesOldAndNewNumbers.get(operand.variableNumber as number) as number;
        }
    });
}

const prepareVariablesMap = (globalVariables: ReportVariable[], globalModuleVariables: ReportVariable[]): Map<number, number> => {

    let map = new Map<number, number>();

    globalVariables.forEach((variable) => {

        let globalvariable = globalModuleVariables.find(x => x.id === variable.globalVariableModuleId) as ReportVariable;

        map.set(globalvariable.number as number, variable.number as number);
    });

    return map;
}

const addMissingVariables = (missingVariables: ReportVariable[], resultVariables: ReportVariable[], variablesOldAndNewNumbers: Map<number, number>) => {
    missingVariables.forEach((variable) => {

        variable.globalVariableModuleId = variable.id as number;

        variable.errors = [];

        variable.mode = Mode.Global;

        variable.isReadOnly = true;

        resultVariables.push(variable);

        updateVariableAssignmentForLookups(variable, variablesOldAndNewNumbers);
    });

}

const addMainVariable = (mainVariable: ReportVariable, resultVariables: ReportVariable[], lookupsOldAndNewNumbers: Map<number, number>) => {

    updateMainVariableAssignment(mainVariable, new Map<number, number>(), lookupsOldAndNewNumbers);

    resultVariables.push(mainVariable);
}

const addOldVariableAtThisIndex = (variables: ReportVariable[], index: number, resultVariables: ReportVariable[]) => {
    resultVariables.push(variables[index]);
}

const prepareVariableNumberChanges = (variables: ReportVariable[]): Map<number, number> => {

    let oldAndNewNumbers = new Map<number, number>();

    for (let index = 0; index < variables.length; index++) {

        let number = index + 1;

        let variable = variables[index] as ReportVariable;

        oldAndNewNumbers.set(variable.number, number);

        variables[index].number = number;
    }

    return oldAndNewNumbers;
}

const prepareLookups = (missingLookups: Lookup[], reportConfig: ReportConfig, mainLookup?: Lookup, oldAndNewNumbers?: Map<number, number>): Lookup[] => {

    if (!oldAndNewNumbers) {
        oldAndNewNumbers = new Map<number, number>();
    }

    let reportConfigLookups = reportConfig.lookups.filter(x => x.globalLookupModuleId !== mainLookup?.globalLookupModuleId);

    let maxNumberInLookups = getHightestNumberInCollection(reportConfigLookups as IdNameAndNumber[]);

    let lookups = prepareMissingLookups(maxNumberInLookups, oldAndNewNumbers, missingLookups);

    if (mainLookup) {

        mainLookup.number = lookups.length + maxNumberInLookups + 1;

        let indexOfLookup = reportConfig.lookups.findIndex(x => x.globalLookupModuleId === mainLookup.globalLookupModuleId) as number;

        let lookupsBeforeTargetLookup = reportConfig.lookups.slice(0, indexOfLookup);

        let lookupsAfterTargetLookup = reportConfig.lookups.slice(indexOfLookup + 1);

        let lookupsBeforeMain = lookupsBeforeTargetLookup.concat(lookups);

        lookups = lookupsBeforeMain.concat(mainLookup).concat(lookupsAfterTargetLookup);
    }

    return lookups;
}

const prepareMissingLookups = (maxNumberInLookups: number, oldAndNewNumbers: Map<number, number>, missingLookups: Lookup[]): Lookup[] => {

    let missingLookupsCopy = copyArray(missingLookups);
    sortingHelper.sortByNumberAsc(missingLookupsCopy as IdNameAndNumber[]);

    missingLookupsCopy.forEach((lookup) => {
        maxNumberInLookups++;

        lookup.globalLookupModuleId = lookup.id as number;

        lookup.id = null;

        lookup.mode = Mode.Global;

        lookup.isReadOnly = true;

        oldAndNewNumbers.set(lookup.number, maxNumberInLookups);

        lookup.number = maxNumberInLookups;
    });

    return missingLookupsCopy;
}

const prepareVariables = (variables: ReportVariable[],
    missingVariables: ReportVariable[],
    mainVariable: ReportVariable,
    insertionNumber: number,
    lookups: Lookup[],
    lookupsOldAndNewNumbers: Map<number, number>
): ReportVariable[] => {

    sortingHelper.sortByNumberAsc(missingVariables);

    sortingHelper.sortByNumberAsc(variables);

    let newVariablesStartIndex = insertionNumber - missingVariables.length > 1 ?
        insertionNumber - missingVariables.length - 1 : 0;

    let resultVariables = [] as ReportVariable[];

    if (missingVariables.length > 0) {

        for (let index = 0; index < variables.length; index++) {

            if (missingVariables.length > 0 && (newVariablesStartIndex === index || newVariablesStartIndex + missingVariables.length <= index)) {

                addMissingVariables(missingVariables, resultVariables, lookupsOldAndNewNumbers);

                addMainVariable(mainVariable, resultVariables, lookupsOldAndNewNumbers);

                if (index < variables.length - 1) {
                    addOldVariableAtThisIndex(variables, index, resultVariables);

                    index += 1;

                    missingVariables = [];
                }
            }
            else {
                resultVariables.push(variables[index]);
            }
        }

        return resultVariables;
    }
    else {
        addMainVariable(mainVariable, resultVariables, lookupsOldAndNewNumbers);

        return variables.concat(resultVariables);
    }
}

const prepareMissingVariables = (missingVariables: ReportVariable[]): ReportVariable[] => {

    let missingVariablesCopy = copyArray(missingVariables);
    sortingHelper.sortByNumberAsc(missingVariablesCopy as IdNameAndNumber[]);

    missingVariablesCopy.forEach((variable) => {

        variable.globalVariableModuleId = variable.id as number;

        variable.id = null;

        variable.errors = [];

        variable.mode = Mode.Global;

        variable.isReadOnly = true;
    });

    return missingVariablesCopy;
}

const prepareWarningMessageOnGlobalVariableAdd = (dataSources: Map<number, string>, referencedLookups: Lookup[], referencedVariables: ReportVariable[], referencedFunctions: UserDefinedFunction[]): string => {

    let message = '';

    let missingDataSources = Array.from(dataSources.values()).map(x => `"${x}"`).join(', ');

    let missingLookups = referencedLookups.map(x => `"${x.name}"`).join(', ');

    let missingVariables = referencedVariables.map(x => `"${x.name}"`).join(', ');

    let missingFunctions = referencedFunctions.map(x => `"${x.name}"`).join(', ');

    if (missingDataSources || missingLookups || missingVariables || missingFunctions) {

        let variablesMessage = missingVariables ? `Please add variables: ${missingVariables}.\n` : '';

        let dataSourcesMessage = missingDataSources ? `Please add datasources: ${missingDataSources}.\n` : '';

        let lookupsMessage = missingLookups ? `Please add referenced Lookups ${missingLookups}.\n` : '';

        let functionssMessage = missingFunctions ? `Please add referenced Functions ${missingFunctions}.` : '';

        message = `${variablesMessage} ${dataSourcesMessage} ${lookupsMessage} ${functionssMessage}\n\n`
    }

    return message;
}


const getReferencesInGlobalConfig = (globalConfig: GlobalModuleConfig,
    number: number,
    dataSourceIds: Set<number>,
    resultVariables: ReportVariable[],
    resultLookups: Lookup[],
    resulFunctions: UserDefinedFunction[],
    existingInReportConfigLookupsIds: Set<number>,
    existingInReportConfigVariablesIds: Set<number>,
    existingInReportConfigUdfsIds: Set<number>,
): void => {

    let variable = globalConfig.variables.find(x => x.number === number);

    if (variable?.assignment) {
        getByAssignmentReferencesInGlobalConfig(globalConfig,
            variable.assignment,
            resultLookups,
            resulFunctions,
            dataSourceIds,
            existingInReportConfigLookupsIds,
            existingInReportConfigUdfsIds,
            existingInReportConfigVariablesIds,
            resultVariables
        );
    }
}

const updateMainVariableAssignment = (variable: ReportVariable, oldAndNewNumbers: Map<number, number>, lookupsOldAndNewNumbers: Map<number, number>) => {
    operandHelper.proceedWhitAssignment(variable.assignment, (operand: Operand) => {
        if (operand.variableNumber as number > 0 && oldAndNewNumbers.has(operand.variableNumber as number)) {

            operand.variableNumber = oldAndNewNumbers.get(operand.variableNumber as number) as number;
        }

        if (operand.lookupNumber) {
            updateVariableAssignmentForLookups(variable, lookupsOldAndNewNumbers);
        }
    });
}

const dataTypeChangeMessage = 'Changing the data type will reset the logic for this variable. Please copy the logic to a new variable before continuing.';

export {
    resolveSystemVariableNameSuggestion,
    prepareOptionsFromVariables,
    dataTypeChangeMessage,
    prepareOptionsFromGlobalVariables,
    prepareWarningMessageOnGlobalVariableAdd,
    getReferencesInGlobalConfig,
    prepareMissingVariables,
    findReferencedComponentsInVariables,
    addCurentGlobalLookupNumbersToMap,
    prepareGlobalVariables,
    getVariableReferences,
    prepareVariables,
    prepareVariableNumberChanges,
    prepareVariablesMap,
    updateVariableAssignmentForLookups,
    updateVariableAssignmentForVariables,
    prepareLookups
};
